import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { faArrowRight, faEye, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../../styles/profile/CouponCard.scss';
import burger from '../../assets/foodfight_burger.png';
import { fetchPrizeOfferDetail } from '../../global/request/prizeOffers'; // import your detail fetch
import SummaryPopup from './SummaryPopup';

export default function CouponCard({ coupon, onRedeem, redeemed, redemptionDate, bearerToken }) {
  const {
    expire = '',
    placeIcon = '',
    itemName = '',
    itemAmount = 0,
    placeName = '',
    placeLocation = '',
    itemCustomization = {},
    matchID = '',
    status = '',
    id = '',
  } = coupon || {};

  const formattedRedemptionDate = redemptionDate
    ? new Date(redemptionDate).toLocaleString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '';

  const formattedExpire = expire ? new Date(expire).toLocaleDateString() : 'N/A';

  const [fillWidth, setFillWidth] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [countdown, setCountdown] = useState({});
  const [detailedCoupon, setDetailedCoupon] = useState(null); // added state for detailed coupon

  const controls = useAnimation();
  const sliderRef = useRef(null);
  const [maxDrag, setMaxDrag] = useState(0);

  // Handle not-yet-ready coupons
  if (status.toLowerCase() === 'pending') {
    return (
      <div className='pending-card' style={{ display: 'none' }}>
        <img className='pending-card-burger' src={burger} alt='burger' />
        <p>Check back when the fight completed!</p>
        <p>
          <strong>ID:</strong> {id}
        </p>
      </div>
    );
  }

  const handleDrag = (_, info) => {
    const currentX = Math.max(0, Math.min(info.offset.x, maxDrag));
    const percentage = (currentX / maxDrag) * 100;
    setFillWidth(percentage);
  };

  const handleDragEnd = (_, info) => {
    if (info.point.x > maxDrag * 0.8) {
      setFillWidth(100);
      setShowPopup(true);
    } else {
      setFillWidth(0);
      controls.start({ x: 0 });
    }
  };

  const confirmRedeem = () => {
    setTimeout(() => {
      onRedeem(coupon);
      setShowPopup(false);
    }, 300);
  };

  const cancelRedeem = () => {
    if (!redeemed) {
      setFillWidth(0);
      controls.start({ x: 0 });
    }
    setTimeout(() => {
      setShowPopup(false);
      setDetailedCoupon(null); // clear detail when closing popup
    }, 300);
  };

  // Fetch detailed coupon info when clicking eye button (only for redeemed)
  const handleViewDetails = async () => {
    if (!bearerToken) {
      console.warn('No bearer token provided for fetching coupon detail.');
      setShowPopup(true); // still show popup with basic info
      return;
    }
    try {
      const detail = await fetchPrizeOfferDetail(bearerToken, id);
      console.log('🎯 Unwrapped detailed coupon:', detail);
      setDetailedCoupon(detail);
      setShowPopup(true);
    } catch (err) {
      console.error('Failed to fetch detailed coupon:', err);
      setShowPopup(true); // fallback to popup without detail
    }
  };

  useEffect(() => {
    let interval;
    if (showPopup && !redeemed) {
      const expiryDate = new Date(expire);
      interval = setInterval(() => {
        const now = new Date();
        const diff = expiryDate - now;
        if (diff <= 0) {
          setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          clearInterval(interval);
        } else {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
          const minutes = Math.floor((diff / (1000 * 60)) % 60);
          const seconds = Math.floor((diff / 1000) % 60);
          setCountdown({ days, hours, minutes, seconds });
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [showPopup, expire, redeemed]);

  useEffect(() => {
    if (sliderRef.current) {
      setMaxDrag(sliderRef.current.offsetWidth - 48);
    }
  }, []);

  return (
    <>
      <div className={`coupon-card-container ${redeemed ? 'greyed-out' : ''}`}>
        <div className='coupon-card-wrapper'>
          <div className='top-bar'>
            {!redeemed ? (
              <div className='expire-element'>Expires: {formattedExpire}</div>
            ) : (
              <button type='button' className='eye-button' onClick={handleViewDetails} aria-label='View Summary'>
                <FontAwesomeIcon icon={faEye} />
              </button>
            )}
          </div>
          <div className='coupon-detail-section'>
            <div className='coupon-icon-block'>
              <img
                src={placeIcon || burger}
                alt='Place Icon'
                className={`venue-icon ${redeemed ? 'opacity-down' : ''}`}
              />
            </div>
            <div className={`item-data-block ${redeemed ? 'opacity-down' : ''}`}>
              <div className='item'>
                {itemName} x{itemAmount}
              </div>
              <div className='place-info'>
                <p className='place-info-name strong-text'>{placeName}</p>
                <p className='place-info-name'>{placeLocation}</p>
              </div>
            </div>
          </div>
          <div className='slider-container' style={{ '--fill-width': `${fillWidth}%` }} ref={sliderRef}>
            {!redeemed ? (
              <div className='slider-track'>
                <div className='slider-fill' />
                <motion.div
                  className='slider-thumb'
                  drag='x'
                  dragConstraints={{ left: 0, right: maxDrag }}
                  onDrag={handleDrag}
                  onDragEnd={handleDragEnd}
                  animate={controls}
                  whileTap={{ scale: 1.05 }}>
                  <div className='slider-icon'>
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </motion.div>
              </div>
            ) : (
              <div className='slider-track disabled' />
            )}
            <div className='slider-label'>
              {redeemed ? `Coupon Redeemed on ${formattedRedemptionDate}` : 'Slide to Redeem'}
            </div>
          </div>
        </div>
      </div>

      {showPopup && (
        <SummaryPopup>
          <div className='summary-popup'>
            <div className='popup-header' style={{ backgroundColor: redeemed ? '#6b6b6b' : '#ff451a' }}>
              <span />
              <span>
                <p className='coupon-summary-header'>{redeemed ? 'Redeemed Coupon' : 'Redeem Now?'}</p>
              </span>
              <span className='right'>
                <button type='button' className='close-btn' onClick={cancelRedeem}>
                  <FontAwesomeIcon icon={faCircleXmark} />
                </button>
              </span>
            </div>

            <div className='popup-content'>
              <div className='popup-section'>
                <div className='expire-date-data'>
                  <p className='expire-detail'>
                    {redeemed ? (
                      ' '
                    ) : (
                      <>
                        Expires: <b>{formattedExpire}</b>
                      </>
                    )}
                  </p>
                </div>

                <div className='venue-data'>
                  <img src={placeIcon || burger} alt='icon' className='venue-icon-summary' />
                  <div>
                    <div className='venue-name'>{placeName}</div>
                    <div>{placeLocation}</div>
                  </div>
                </div>

                <div className='items-data'>
                  <p style={{ fontWeight: 'normal' }}>Item Details:</p>
                  <p className='item-name'>
                    {itemName} x{itemAmount}
                  </p>
                  {itemCustomization && Object.keys(itemCustomization).length > 0 && (
                    <ul className='item-adjustment'>
                      {Object.entries(itemCustomization).map(([key, value]) => (
                        <li key={key}>
                          <span className='cutomized-detail'>
                            <p className='customized-key'>{key}:</p> <p>{value}</p>
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div className='countdown-section'>
                <p className='countdown-title' style={{ fontWeight: 'normal' }}>
                  {!redeemed ? 'Expiring In:' : 'Coupon Redeemed on:'}
                </p>
                {!redeemed ? (
                  <div className='timer-block'>
                    {['days', 'hours', 'minutes', 'seconds'].map(unit => (
                      <span className='timer-box' key={unit}>
                        <p className='timer-content'>{countdown[unit]}</p>
                        <p className='timer-category'>{unit.charAt(0).toUpperCase() + unit.slice(1)}</p>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div>
                    <p className='redeemed-format'> {formattedRedemptionDate}</p>
                  </div>
                )}
              </div>

              {!redeemed && coupon.warning && (
                <div className='warning-section'>
                  <img src={coupon.warning.image} alt='Warning' />
                  <div className='warning-content'>
                    <h4>{coupon.warning.title}</h4>
                    <p>{coupon.warning.message}</p>
                  </div>
                </div>
              )}

              {/* Show detailed coupon JSON if fetched */}
              {detailedCoupon && <pre className='detailed-coupon-json'>{JSON.stringify(detailedCoupon, null, 2)}</pre>}

              <div className='popup-buttons-section'>
                {!redeemed && (
                  <>
                    <button type='button' onClick={cancelRedeem} className='later-btn'>
                      Later
                    </button>
                    <button type='button' onClick={confirmRedeem} className='redeem-btn'>
                      REDEEM AWAY!
                    </button>
                  </>
                )}
              </div>

              <div className='footer-section'>
                <div className='id-content'>
                  <p>ID: {matchID}</p>
                </div>
              </div>
            </div>
          </div>
        </SummaryPopup>
      )}
    </>
  );
}
